import { AxiosInstance, AxiosResponse } from 'axios'
import _ from 'lodash'
import { all, call, getContext, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { apiCall } from '../../../utils/api'
import { mapActionError, filteredFilters } from '../../../utils/helpers'
import { downloadExportViaToken, waitForConfigLoad } from '../../../utils/sagaUtils'
import { downloadFile } from '../../../utils/download'
import { Dictionary } from '../../../utils/types'
import { getAccountReportsPageSize, getCountryCode } from '../../config'
import { withLoading, withLoadingParametrized } from '../../loading'
import { LogActions } from '../../logs'
import { ColumnSorting, TableActions, sortParam } from '../../table'
import { selectSorting } from '../../table/selectors'
import { getVisibleDialogData } from '../../dialog/selectors'
import { DialogActions } from '../../../redux/dialog'
import { DialogType } from '../../../redux/dialog/types'

import { AccountReportsActions } from './actions'
import { getAccountReportFilters, getAccountReportsFilter } from './selectors'
import { AccountReport, DeliveryType, DocumentType, StatusCode, PdfOpenMode } from './types'

type Document = {
  id?: string
  contentType?: string
}

type ReportsResponse = {
  reports: {
    id: string
    company: string
    countryCode: string
    customerReference: string
    officeCode: string
    orderDate: string
    typeCode: string
    expirationDate?: string
    isValid: boolean
    documents: Document[]
    statusCode: string
    deliveryType: string
    productName: string
    productCode: string
    deliveryDate: string
    isFallbackCountry: boolean
    externalSystemId?: string
  }[]
  meta: {
    totalCount: number
    pageCount: number
    pageNumber: number
  }
}

const mapResponseToAccountReport = (res: ReportsResponse): AccountReport[] => {
  const documentTypes = ['PDF', 'PDF_URL', 'TIFF']

  return _.map(res.reports, x => ({
    ...x,
    htmlDocumentId: _.find(x.documents, x => x.contentType === 'HTML')?.id,
    documentId: _.find(x.documents, x => _.includes(documentTypes, x.contentType))?.id,
    documentType: mapDocumentType(_.find(x.documents, x => _.includes(documentTypes, x.contentType))?.contentType),
    statusCode: x.statusCode as StatusCode,
    deliveryType: x.deliveryType as DeliveryType,
    externalSystemId: x.externalSystemId
  }))
}

const mapDocumentType = (documentType?: string) => {
  const documentTypeMapper: Dictionary<DocumentType> = {
    PDF: DocumentType.Pdf,
    PDF_URL: DocumentType.Pdf,
    TIFF: DocumentType.Tiff
  }

  return documentType ? documentTypeMapper[documentType] : undefined
}

const preprocessFilters = (filters: Dictionary<any>) =>
  _.mapValues(filters, filter => (_.has(filter, 'value') ? _.get(filter, 'value') : filter))

export function* exportReports({ payload }: ReturnType<typeof AccountReportsActions.exportReports>) {
  yield call(waitForConfigLoad)

  const countryCode: string = yield select(getCountryCode)
  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'archiveReports' })
  const filter: string = yield select(getAccountReportsFilter)
  const filters: Dictionary<any> = yield select(getAccountReportFilters)
  const nonEmptyFilters = filteredFilters(filters)
  const preprocessedFilters = preprocessFilters(nonEmptyFilters)

  try {
    yield call(apiCall, {
      url: '/api/Report/Export',
      method: 'post',
      params: {
        domainCode: countryCode,
        page: 1,
        pageSize: 1,
        searchOrder: sortParam(sorting),
        search: filter || undefined
      },
      data: {
        name: payload.name,
        filter: _.every(_.values(preprocessedFilters), _.isEmpty) ? {} : preprocessedFilters,
        columns: _.map(payload.columns, x => ({ id: x.id, name: x.name })),
        productsTranslations: _.map(payload.umbracoProducts, x => ({ code: x.bifCode, description: x.name })),
        domains: payload.domains
      }
    })
  } catch (e) {
    const visibleDialog: DialogType = yield select(getVisibleDialogData)
    if (visibleDialog === 'ArchiveExportDialog')
      yield put(
        DialogActions.updateDialogData({
          dialog: 'ArchiveExportDialog',
          data: {
            customColumns: {},
            isError: true
          }
        })
      )
    else {
      yield put(
        DialogActions.showDialogWithData({
          dialog: 'ArchiveExportDialog',
          data: {
            customColumns: {},
            isError: true
          }
        })
      )
    }
  }
}

export function* fetchReports(action: ReturnType<typeof AccountReportsActions.fetchData>) {
  yield call(waitForConfigLoad)
  const { payload } = action

  const countryCode: string = yield select(getCountryCode)
  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'archiveReports' })
  const filter: string = yield select(getAccountReportsFilter)
  const filters: Dictionary<any> = yield select(getAccountReportFilters)

  const nonEmptyFilters = filteredFilters(filters)
  const preprocessedFilters = preprocessFilters(nonEmptyFilters)

  const { data }: AxiosResponse<ReportsResponse> = yield call(apiCall, {
    url: '/api/Report/Get',
    method: 'post',
    params: {
      domainCode: countryCode,
      page: payload.page,
      pageSize: payload.pageSize,
      searchOrder: sortParam(sorting),
      search: filter || undefined
    },
    data: _.every(_.values(preprocessedFilters), _.isEmpty) ? {} : preprocessedFilters
  })

  yield put(
    AccountReportsActions.setData({
      data: mapResponseToAccountReport(data),
      page: data.meta.pageNumber,
      totalPages: data.meta.pageCount
    })
  )

  if (data.meta.pageNumber === 1) {
    yield put(
      TableActions.setPagination({
        tableKey: 'archiveReports',
        pagination: {
          current: data.meta.pageNumber,
          pageSize: payload.pageSize,
          total: data.meta.totalCount
        }
      })
    )
  }
}

export function* loadReport(action: ReturnType<typeof AccountReportsActions.loadReport>) {
  const { payload } = action

  const { data }: AxiosResponse = yield call(apiCall, {
    url: `/api/Download/report?id=${payload.documentId}&token=${payload.tokenId}`,
    method: 'get'
  })

  yield put(
    AccountReportsActions.setReport({
      document: data,
      documentId: payload.documentId,
      customerRequestId: payload.customerRequestId,
      pdfDownloadReportName: payload.pdfDownloadReportName
    })
  )
}

export function* reloadOnSort(action: ReturnType<typeof TableActions.setSorting>) {
  if (action.payload.tableKey !== 'archiveReports') return

  const filter: string = yield select(getAccountReportsFilter)
  const pageSize: number = yield select(getAccountReportsPageSize)

  yield put(
    AccountReportsActions.fetchData({
      filter,
      page: 1,
      pageSize
    })
  )
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'archiveReports') return

  const filter: string = yield select(getAccountReportsFilter)
  const { pagination } = payload

  yield put(
    AccountReportsActions.fetchData({
      filter,
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

export function* reloadOnFiltersChange() {
  const filter: string = yield select(getAccountReportsFilter)
  const pageSize: number = yield select(getAccountReportsPageSize)

  yield put(
    AccountReportsActions.fetchData({
      filter,
      page: 1,
      pageSize
    })
  )
}

export function* downloadExport({ payload }: ReturnType<typeof AccountReportsActions.downloadExport>) {
  const { exportId } = payload

  yield call(downloadExportViaToken, exportId)
  return
}

export function* downloadReport({ payload }: ReturnType<typeof AccountReportsActions.downloadReport>) {
  const { customerRequestId, deliveryType, documentType, downloadReportName, pdfOpenMode } = payload

  if (deliveryType === DeliveryType.Token) {
    yield call(downloadReportViaToken, customerRequestId, documentType, downloadReportName, pdfOpenMode)
    return
  }

  if (deliveryType === DeliveryType.Url) {
    yield call(downloadReportViaUrl, customerRequestId, downloadReportName, pdfOpenMode)
    return
  }

  yield put(LogActions.addLogItem(mapActionError(`Unknown delivery type: ${payload.deliveryType}`, 'ERROR')))
}

export function* downloadReportViaToken(
  id: string,
  documentType: DocumentType,
  downloadReportName: string,
  pdfOpenMode?: PdfOpenMode
) {
  const axios: AxiosInstance = yield getContext('axios')

  const {
    data
  }: AxiosResponse<{
    token: string
    documentId: string
    expirationDate: string
  }> = yield call(apiCall, {
    url: '/api/Download/token',
    method: 'get',
    params: {
      customerRequestId: id,
      contentType: documentType
    }
  })

  if (documentType !== DocumentType.Html) {
    if (pdfOpenMode === PdfOpenMode.NewTab) {
      //open PDF in next tab for skyminder
      window.open(`${axios.defaults.baseURL || ''}/api/Download/report?id=${data.documentId}&token=${data.token}`)
    } else {
      yield call(
        downloadFile,
        `${axios.defaults.baseURL || ''}/api/Download/report?id=${data.documentId}&token=${data.token}`,
        document.body,
        downloadReportName
      )
    }
  }

  if (documentType === DocumentType.Html) {
    const tokenId = data.token
    const documentId = data.documentId
    const customerRequestId = id

    yield put(
      AccountReportsActions.loadReport({
        documentId,
        tokenId,
        customerRequestId,
        pdfDownloadReportName: downloadReportName
      })
    )
  }
}

export function* downloadReportViaUrl(documentId: string, downloadReportName: string, pdfOpenMode?: PdfOpenMode) {
  const {
    data
  }: AxiosResponse<{
    url: string
  }> = yield call(apiCall, {
    url: '/api/Download/report/url',
    method: 'get',
    params: {
      id: documentId
    }
  })

  if (!data.url) {
    // eslint-disable-next-line no-console
    console.error(`No data URL retrieved for document ${documentId}`)
    return
  }
  if (pdfOpenMode === PdfOpenMode.NewTab) {
    //open PDF in next tab for skyminder
    window.open(data.url)
  } else {
    yield call(downloadFile, data.url, document.body, downloadReportName)
  }
}

export function* loadReportDetail(action: ReturnType<typeof AccountReportsActions.reportsLoadDetail>) {
  const { reportId, type } = action.payload

  const { data }: AxiosResponse<any> = yield call(apiCall, {
    url: '/api/Report/Info',
    method: 'get',
    params: {
      id: reportId
    }
  })

  yield put(
    AccountReportsActions.setReportDetail({
      reportId,
      report: { ...data, documentType: mapDocumentType(data.documentType) },
      type
    })
  )
}

/* istanbul ignore next */
export function* accountReportsSagas() {
  yield all([
    takeLatest(getType(AccountReportsActions.fetchData), withLoading('accountReports', fetchReports)),
    takeLatest(getType(TableActions.setSorting), reloadOnSort),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange),
    takeLatest(getType(AccountReportsActions.downloadReport), downloadReport),
    takeLatest(getType(AccountReportsActions.downloadExport), downloadExport),
    takeLatest(getType(AccountReportsActions.exportReports), exportReports),
    takeLatest(getType(AccountReportsActions.loadReport), loadReport),
    takeEvery(
      getType(AccountReportsActions.reportsLoadDetail),
      withLoadingParametrized('accountReports', loadReportDetail, x => x.payload.reportId)
    ),
    takeLatest(getType(AccountReportsActions.setFilters), reloadOnFiltersChange),
    takeLatest(getType(AccountReportsActions.clearFilter), reloadOnFiltersChange)
  ])
}
